import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        url: String
    }

    static targets = ["input", "results", "hidden"]

    connect() {
        this.abortController = null
        this.hideResults()
    }

    disconnect() {
        if (this.abortController) this.abortController.abort()
    }

    async search() {
        const query = this.inputTarget.value.trim()

        if (query === "") {
            this.clearResults()
            this.hideResults()
            return
        }

        if (this.abortController) this.abortController.abort()
        this.abortController = new AbortController()

        try {
            const url = new URL(this.urlValue, window.location.origin)
            url.searchParams.set("q", query)

            const response = await fetch(url, { signal: this.abortController.signal })
            if (!response.ok) return

            const products = await response.json()
            this.renderResults(products)
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error("Product search failed", error)
            }
        }
    }

    renderResults(products) {
        this.clearResults()

        if (!products || products.length === 0) {
            this.resultsTarget.insertAdjacentHTML(
                "beforeend",
                `<div class="px-3 py-2 text-sm text-gray-500">No results</div>`
            )
            this.showResults()
            return
        }

        products.forEach((product) => {
            const namePart = product.name ? `-${product.name}` : ""
            const sizePart = product.size ? `-${product.size}` : ""
            const label = `[${product.series.name}]${namePart}${sizePart}`

            const item = document.createElement("button")
            item.type = "button"
            item.className = "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
            item.textContent = label

            item.addEventListener("mousedown", (event) => {
                event.preventDefault()
                this.selectProduct(product, label)
            })

            this.resultsTarget.appendChild(item)
        })

        this.showResults()
    }

    selectProduct(product, label) {
        this.inputTarget.value = label
        this.hiddenTarget.value = product.id
        
        // We need to set the data-is-bom attribute on the hidden field 
        // because quotation_lines_row_controller looks at the selected option's dataset.
        // But since we are replacing a select with a hidden field, we should 
        // probably update quotation_lines_row_controller to look at the hidden field's dataset
        // or just set it here.
        this.hiddenTarget.dataset.isBom = product["is_bom?"] ? "true" : "false"

        this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
        this.hideResults()
    }

    clearResults() {
        this.resultsTarget.innerHTML = ""
    }

    showResults() {
        this.positionResults()
        this.resultsTarget.classList.remove("hidden")
        
        // Listen to window events to reposition if necessary
        this.repositionHandler = () => this.positionResults()
        window.addEventListener("scroll", this.repositionHandler, true)
        window.addEventListener("resize", this.repositionHandler)
    }

    hideResults() {
        this.resultsTarget.classList.add("hidden")
        if (this.repositionHandler) {
            window.removeEventListener("scroll", this.repositionHandler, true)
            window.removeEventListener("resize", this.repositionHandler)
            this.repositionHandler = null
        }
    }

    positionResults() {
        const rect = this.inputTarget.getBoundingClientRect()
        const results = this.resultsTarget

        // Use fixed positioning to break out of overflow: auto containers
        results.style.top = `${rect.bottom}px`
        results.style.left = `${rect.left}px`
        results.style.width = `${rect.width}px`
        results.style.zIndex = "9999"
    }

    maybeHide() {
        setTimeout(() => {
            if (!this.element.contains(document.activeElement)) {
                this.hideResults()
            }
        }, 150)
    }
}
